<template>
    <div class="prediction-event-detail">
        <!-- SSR slot content: Blade HTML rendered for SEO, removed once Vue mounts -->
        <div v-if="showSeoContent"><slot></slot></div>

        <!-- Chart: Outcome Probabilities Bar Chart (top 10) -->
        <div class="compass-card mb-4">
            <h5 class="mb-3" style="font-weight: 600;">Outcome Probabilities</h5>
            <div class="prediction-outcomes-chart">
                <div v-for="m in sortedMarkets.slice(0, 10)" :key="m.market_id || m.id"
                     class="outcome-row" @click="selectMarket(m)">
                    <div class="outcome-label" :title="m.market_title">
                        <img v-if="m.market_image_url" :src="m.market_image_url" alt=""
                             class="outcome-avatar">
                        <span>{{ m.market_title }}</span>
                    </div>
                    <div class="outcome-bar-container">
                        <div class="outcome-bar"
                             :class="{ 'outcome-bar--selected': selectedMarketId === (m.market_id || m.id) }"
                             :style="{ width: Math.max(2, m.yes_price * 100) + '%' }">
                        </div>
                        <span class="outcome-value font-mono-num">{{ formatPct(m.yes_price * 100) }}</span>
                    </div>
                </div>
                <div v-if="sortedMarkets.length > 10" class="analytics-text-subtle" style="font-size: 0.8rem; text-align: right; padding-top: 4px;">
                    +{{ sortedMarkets.length - 10 }} more — see table below
                </div>
            </div>
        </div>

        <!-- Price History Chart (hidden when no data on multi-market events) -->
        <div class="compass-card mb-4" v-if="selectedMarket && (hasHistory || isSingleMarket)">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 style="font-weight: 600; margin: 0;">
                        {{ isSingleMarket ? 'Price History' : selectedMarket.market_title }}
                    </h5>
                    <div class="text-muted" style="font-size: 0.8rem;">
                        Yes/No price over time
                    </div>
                </div>
                <div class="d-flex gap-2 align-items-center">
                    <span class="font-mono-num" style="color: var(--compass-success); font-weight: 600;">
                        Yes {{ formatPct(selectedMarket.yes_price * 100) }}
                    </span>
                    <span style="color: #d1d5db;">|</span>
                    <span class="font-mono-num" style="color: var(--compass-danger); font-weight: 600;">
                        No {{ formatPct(selectedMarket.no_price * 100) }}
                    </span>
                </div>
            </div>

            <div ref="chartContainer" style="height: 250px; position: relative;">
                <canvas ref="priceChart"></canvas>
                <div v-if="!hasHistory" class="d-flex align-items-center justify-content-center text-muted" style="position: absolute; inset: 0; font-size: 0.9rem;">
                    <div class="text-center">
                        <i class="fas fa-chart-line mb-2" style="font-size: 1.5rem; opacity: 0.3;"></i>
                        <div>Price history will appear as data is collected over time.</div>
                        <div style="font-size: 0.8rem; margin-top: 4px;">Snapshots recorded every 15 minutes.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Odds Bar for single-market events -->
        <div v-if="isSingleMarket" class="compass-card mb-4">
            <h5 class="mb-3" style="font-weight: 600;">Current Odds</h5>
            <div style="display: flex; height: 16px; border-radius: 8px; overflow: hidden;">
                <div :style="{ width: (sortedMarkets[0].yes_price * 100) + '%', background: 'var(--compass-success)' }"></div>
                <div :style="{ width: (sortedMarkets[0].no_price * 100) + '%', background: 'var(--compass-danger)' }"></div>
            </div>
            <div class="d-flex justify-content-between mt-2" style="font-size: 0.9rem;">
                <span style="color: var(--compass-success); font-weight: 600;">
                    Yes {{ formatPct(sortedMarkets[0].yes_price * 100) }}
                </span>
                <span style="color: var(--compass-danger); font-weight: 600;">
                    No {{ formatPct(sortedMarkets[0].no_price * 100) }}
                </span>
            </div>
        </div>

        <!-- All Outcomes Table -->
        <div class="compass-card mb-4">
            <h5 class="mb-3" style="font-weight: 600;">
                {{ isSingleMarket ? 'Market Details' : 'All Outcomes' }}
                <span class="text-muted" style="font-size: 0.8rem; font-weight: 400;">
                    ({{ allMarkets.length }})
                </span>
            </h5>
            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Outcome</th>
                            <th class="text-end">Yes</th>
                            <th class="text-end">No</th>
                            <th class="text-end">Volume</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="m in visibleMarkets" :key="m.market_id || m.id"
                            :class="{ 'table-active': selectedMarketId === (m.market_id || m.id) }"
                            style="cursor: pointer;"
                            @click="selectMarket(m)">
                            <td>
                                <div class="d-flex align-items-center gap-2">
                                    <img v-if="m.market_image_url" :src="m.market_image_url" alt=""
                                         style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover;">
                                    <span style="font-weight: 500;">{{ m.market_title }}</span>
                                </div>
                            </td>
                            <td class="td-num" style="color: var(--compass-success); font-weight: 600;">
                                {{ formatPct(m.yes_price * 100) }}
                            </td>
                            <td class="td-num" style="color: var(--compass-danger);">
                                {{ formatPct(m.no_price * 100) }}
                            </td>
                            <td class="td-num">{{ formatVolume(m.volume_usd) }}</td>
                            <td class="text-end">
                                <a :href="'/app/predict/' + m.slug"
                                   class="compass-btn compass-btn--sm compass-btn--outline"
                                   @click.stop>
                                    Trade
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="sortedMarkets.length > 10 && !showAllOutcomes" class="text-center mt-2">
                <button class="compass-btn compass-btn--sm compass-btn--outline" @click="showAllOutcomes = true">
                    Show All {{ sortedMarkets.length }} Outcomes
                </button>
            </div>
        </div>

        <!-- Your Positions -->
        <div v-if="hasPositions" class="compass-card compass-card--accent mb-4">
            <h5 class="mb-3" style="font-weight: 600;">Your Positions in This Event</h5>
            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Outcome</th>
                            <th>Side</th>
                            <th class="text-end">Avg Price</th>
                            <th class="text-end">Current</th>
                            <th class="text-end">P&L</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="pos in positions" :key="pos.pubkey">
                            <td style="font-weight: 500;">{{ pos.marketTitle || '—' }}</td>
                            <td>
                                <span :class="pos.isYes ? 'text-success' : 'text-danger'" style="font-weight: 600;">
                                    {{ pos.isYes ? 'Yes' : 'No' }} &times;{{ pos.contracts }}
                                </span>
                            </td>
                            <td class="td-num">${{ formatPrice(pos.avgPriceUsd) }}</td>
                            <td class="td-num">${{ formatPrice(pos.markPriceUsd) }}</td>
                            <td class="td-num">
                                <span :class="(pos.unrealizedPnlPct || 0) >= 0 ? 'pct-change pct-positive' : 'pct-change pct-negative'">
                                    {{ (pos.unrealizedPnlPct || 0) >= 0 ? '+' : '' }}{{ formatPct(pos.unrealizedPnlPct) }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useSolSdk } from '../composables/useSolSdk';
import { formatPct, formatPrice, formatVolume } from '../composables/usePredictFormatters';

export default {
    name: 'PredictionEventDetail',
    props: {
        markets: { type: Array, required: true },
        history: { type: Object, default: () => ({}) },
        eventId: { type: String, required: true },
        eventTitle: { type: String, default: '' },
    },
    setup(props) {
        const { getSdk } = useSolSdk();

        const showSeoContent = ref(true);

        const allMarkets = ref(props.markets);
        const selectedMarketId = ref(null);
        const showAllOutcomes = ref(false);
        const positions = ref([]);
        const hasPositions = computed(() => positions.value.length > 0);
        const priceChart = ref(null);
        let chartInstance = null;

        const isSingleMarket = computed(() => allMarkets.value.length === 1);

        const sortedMarkets = computed(() => {
            return [...allMarkets.value].sort((a, b) => (b.yes_price || 0) - (a.yes_price || 0));
        });

        const visibleMarkets = computed(() => {
            if (showAllOutcomes.value || sortedMarkets.value.length <= 10) return sortedMarkets.value;
            return sortedMarkets.value.slice(0, 10);
        });

        const selectedMarket = computed(() => {
            if (!selectedMarketId.value) return sortedMarkets.value[0] || null;
            return allMarkets.value.find(m => (m.market_id || m.id) === selectedMarketId.value) || sortedMarkets.value[0];
        });

        const selectedHistory = computed(() => {
            if (!selectedMarketId.value) {
                const first = sortedMarkets.value[0];
                return first ? (props.history[first.market_id] || []) : [];
            }
            return props.history[selectedMarketId.value] || [];
        });

        const hasHistory = computed(() => selectedHistory.value.length > 1);

        function selectMarket(m) {
            selectedMarketId.value = m.market_id || m.id;
            nextTick(() => renderChart());
        }



        function renderChart() {
            if (!priceChart.value) return;
            if (chartInstance) {
                chartInstance.destroy();
                chartInstance = null;
            }

            const data = selectedHistory.value;
            if (data.length < 2) return;

            const labels = data.map(d => {
                const dt = new Date(d.time);
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                       ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            });

            const Chart = window.Chart;
            if (!Chart) return;

            chartInstance = new Chart(priceChart.value.getContext('2d'), {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Yes',
                            data: data.map(d => (d.yes * 100).toFixed(1)),
                            borderColor: '#22c55e',
                            backgroundColor: 'rgba(34, 197, 94, 0.08)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: data.length > 20 ? 0 : 2,
                            pointHoverRadius: 5,
                            borderWidth: 2,
                        },
                        {
                            label: 'No',
                            data: data.map(d => (d.no * 100).toFixed(1)),
                            borderColor: '#ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: data.length > 20 ? 0 : 2,
                            pointHoverRadius: 5,
                            borderWidth: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                font: { size: 12 },
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.y + '%',
                            },
                        },
                    },
                    scales: {
                        x: {
                            display: true,
                            grid: { display: false },
                            ticks: {
                                maxRotation: 0,
                                maxTicksLimit: 6,
                                font: { size: 11 },
                            },
                        },
                        y: {
                            min: 0,
                            max: 100,
                            grid: {
                                color: 'rgba(139, 92, 246, 0.06)',
                            },
                            ticks: {
                                callback: (val) => val + '%',
                                font: { size: 11 },
                            },
                        },
                    },
                },
            });
        }

        async function loadPositions() {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                positions.value = [];
                return;
            }

            try {
                const sdk = await getSdk();
                const allPositions = await sdk.predict.getPositions(pubkey.toBase58());
                const marketIds = new Set(allMarkets.value.map(m => m.market_id));
                positions.value = (allPositions || []).filter(p => marketIds.has(p.marketId));
            } catch (e) {
                console.error('[PredictionEventDetail] Failed to load positions:', e);
                positions.value = [];
            }
        }

        onMounted(() => {
            // Remove SSR slot content — Vue interactive UI takes over
            showSeoContent.value = false;

            // Default selection
            if (sortedMarkets.value.length) {
                selectedMarketId.value = sortedMarkets.value[0].market_id || sortedMarkets.value[0].id;
            }

            nextTick(() => renderChart());

            if (window.wallet?.publicKey?.value) {
                loadPositions();
            }
        });

        if (window.wallet?.publicKey) {
            watch(() => window.wallet.publicKey.value, (newVal) => {
                if (newVal) loadPositions();
                else positions.value = [];
            });
        }

        return {
            showSeoContent,
            allMarkets,
            sortedMarkets,
            visibleMarkets,
            selectedMarketId,
            selectedMarket,
            showAllOutcomes,
            isSingleMarket,
            hasHistory,
            positions,
            hasPositions,
            priceChart,
            selectMarket,
            formatPct,
            formatPrice,
            formatVolume,
        };
    },
};
</script>

<style scoped>
.prediction-outcomes-chart {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.outcome-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s;
}

.outcome-row:hover {
    background: rgba(139, 92, 246, 0.06);
}

.outcome-label {
    width: 160px;
    flex-shrink: 0;
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 8px;
}

.outcome-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.outcome-bar-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 24px;
}

.outcome-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--compass-purple), rgba(139, 92, 246, 0.6));
    border-radius: 4px;
    min-width: 4px;
    transition: width 0.3s ease;
}

.outcome-bar--selected {
    background: linear-gradient(90deg, var(--compass-purple), var(--compass-pink));
}

.outcome-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--compass-purple);
    min-width: 50px;
    text-align: right;
}

@media (max-width: 576px) {
    .outcome-label {
        width: 110px;
        font-size: 0.8rem;
    }

    .outcome-value {
        font-size: 0.75rem;
        min-width: 40px;
    }
}
</style>
